import { AbstractPaymentProvidersEvaluatorStrategy } from './AbstractPaymentProvidersEvaluatorStrategy';
import { BankTransferPayoutDetails, PayoutTypes } from './PayoutDetails';
import { createPaymentProvider } from './PaymentProvider';
import { PaymentsConfig, TPaymentsConfig } from './transferPaymentsConfig';
import { OrgSettings } from 'features/organization/orgSettings/OrgSettings';
import { offrampPaymentMethodIdMap } from './PaymentProviderIdTypes';
import { MoneyMovementOperationTypes } from './MoneyMovementOperationTypes';

export class BankTransferPaymentProvidersEvaluatorStrategy extends AbstractPaymentProvidersEvaluatorStrategy {
  private _bankPayouts: BankTransferPayoutDetails[];

  constructor(
    paymentProvidersConfig: PaymentsConfig,
    bankPayouts: BankTransferPayoutDetails[],
    platformIntegrations: Record<string, any>,
    organizationSettings: OrgSettings
  ) {
    super(paymentProvidersConfig, platformIntegrations, organizationSettings);
    this._bankPayouts = bankPayouts;
  }

  isBatchPayout(): boolean {
    return this._bankPayouts.length > 1;
  }

  private getFilteredConfiguredProviders(): TPaymentsConfig['providers'] {
    const configuredProvidersAll: TPaymentsConfig['providers'] =
      this.paymentProvidersConfig.get('providers', []);
    const preferredOfframpMethod =
      this.organizationSettings.getPreferredOfframpMethod();
    const configuredOfframpProviders = configuredProvidersAll
      .filter(
        (provider) =>
          provider.operationType === MoneyMovementOperationTypes.Offramp
      )
      .filter(({ id }) => {
        return id === offrampPaymentMethodIdMap[preferredOfframpMethod];
      });

    const configuredProvidersRest = configuredProvidersAll.filter(
      (provider) =>
        provider.operationType !== MoneyMovementOperationTypes.Offramp
    );

    return [...configuredProvidersRest, ...configuredOfframpProviders].filter(
      (provider) => {
        return this.getPayouts().every(
          (p) =>
            !p.inferOperationType() ||
            // if we know source for crypto payout it can be cross chain,m in that case we dont need to sho here onchain options
            p.inferOperationType() === provider.operationType
        );
      }
    );
  }

  getAllPossiblePaymentProviders() {
    if (this.isPayoutEligible()) {
      const configuredProviders = this.getFilteredConfiguredProviders();
      return configuredProviders
        .filter((providerConfig) => {
          const isSuitableForPayouts =
            providerConfig.enabled &&
            providerConfig.suitableForPayouts?.includes(
              PayoutTypes.BankTransfer
            ) &&
            this._bankPayouts.every(
              (p) =>
                providerConfig.suitableForCurrencies?.includes(
                  p.currency as
                    | 'USDC'
                    | 'USDT'
                    | 'EUR'
                    | 'USD'
                    | 'USDC.native_polygon'
                )
            );

          if (this.isBatchPayout()) {
            return isSuitableForPayouts && providerConfig.batchAvailable;
          }

          return isSuitableForPayouts;
        })
        .map((providerConfig) => createPaymentProvider(providerConfig));
    }

    return [];
  }

  getPayouts(): BankTransferPayoutDetails[] {
    return this._bankPayouts;
  }

  isPayoutEligible(): boolean {
    if (
      this._bankPayouts.length &&
      this._bankPayouts.every(
        (payout) => payout.currency === this._bankPayouts[0].currency
      )
    ) {
      return true;
    }

    return false;
  }
}
